// 前端内置 RSA 私钥（与后端公钥配对，PKCS#8 格式）
const PRIVATE_KEY_PEM = `
-----BEGIN PRIVATE KEY-----
MIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQDCNDgxYbbANun4
AsXaB8c12HtEPz9w7dnjGwWrWWXv5ozXc9Wxb86k0ka5I1j5hKftRcDHYGcdXGBc
PQFK1ReOsosOsoJPZkSuwYONk39O3CHaRls+Fy93+HKlEafafhGdFOeEMAoMty4m
atKi7XVuVryh1NyPBAoSwLzGTl31VNkfLlX8+pxG8LMDSH6G78HivdnhGXotGmlX
iBXZ3KzYYT6cjJpapQyF7Dzg2k+5vxJ5DOSQFGahz0X6aD2ITVBg9YhtUcv/wOWg
bdlz5vKCJhswEGu0q8jcWrKyNnt4j1dg5smzFF7RxN879w/PuByCciFDk/Th55YU
nbX3YRcjAgMBAAECggEACRX8sQwYyupmzOSC/DM+oJLLGu4jROB5QApXcWUCg2mp
GjrAUIe3cSJTY723H8loj+MiZKbqfBxXzR5KOAWQj2u5XMvWPpUSzo2diYX76qUa
7QWQpu7abjBQsM9gJ9/xFAUn2xk3nHuvgZBK4DY1jHQraXOLiXdkv0GG3iJJfBJS
I2+KgB3R9sAj8h3jTo0ezPzANwOUkZDwIxhvoaczc8rFP0FAWHcXuCFiu2Y60U3A
xObq5Wr+Oq45+zYoxlX0nDOYtwGJWtVzYLwJ8RQIjcUGh9ksZeNfoJkAdK9QF+tm
ZquA4MqDfK0g5ziBz5mgdvzCcCWXSHik6+iBPxBvjQKBgQDjMcRjGmIUAYCZ47Yb
cfCmyWLpbUg6vmTpDkl6+fF5rs0U/aQtrD/5NdaBWO6kLRJQZjqcEo2ZJJKwVj3r
Pr89H0nxAsF9XzBS45zsg4JBQ4eGzAal4YYSadiT+2th3E4AAx95lS/S0zEYEQjX
eIs8N8BWYAJjfDx2yiJnkNtvTwKBgQDa06fSUvaJlljCeqIzgrmOObCrJV2zuXKU
TrUIffgMfgo+oXfsvVvjzR5/K5wOcV36OQf8O/eEspd3QWH4TCNOuzspqFrCO4cp
pceBCzlge4/Ymaona/FOazhEVuFpL8ISqRwxPe9SaTNdt1RiN3et78fskmJfgWvx
FnVdjGmF7QKBgDSTSb0dV+EFT/tMxNGpFmWiaO9XyMU/Vh7QnZSFzqm4F+FpqNqg
59UF7nPUXrVDcN+GKL4BVR9BZWjFLGMKDDtayEOrvZcDti0YWzIoZLYxqGU7RbaR
b/NG50WngvwMfUhncJs0OPLyyIOnPYKPdLkkta/HXAYls+BRepC45u7lAoGAMOVi
doi7Nfs2Uh585+2p8LHLXDK5QVOK2sDLit468u+m8l+6IFgflENdMSVZdZC3YxYj
RqVPpYMSfT9K2OSKbyk/CwvnW8dZaGD2t0r+wyRY/Bk6AB0Kim9C32Jac9qMDwdi
mU4xj8SaCbLRVDD4uRD/J0l+WcDdkb1m9ERPv/ECgYEAx0dWw6J+XY031bEPW+WQ
jVKvM4OyxgXbeWPhCWgm+C0P2DB8uPKybPVwmf+8/uijiUkJXLJCfXWAMUaZl/1L
fWutjh1nNWQb3rKN2H7GvuOiJ6NLIcA8oUhZoJ34vcVkEeCRRbs3ZMBhGbc9Aoru
QvFQg5UJ0NMN5gLjnvqVmD0=
-----END PRIVATE KEY-----
`

/**
 * 将 PEM 格式私钥导入为 Web Crypto API 可用的 CryptoKey
 */
async function importPrivateKey(): Promise<CryptoKey> {
    // 去掉 PEM 头尾和换行，拿到纯 Base64 内容
    const pemContent = PRIVATE_KEY_PEM
        .replace("-----BEGIN PRIVATE KEY-----", "")
        .replace("-----END PRIVATE KEY-----", "")
        .replace(/\s/g, "")

    const derBuffer = Uint8Array.from(atob(pemContent), c => c.charCodeAt(0))

    return await crypto.subtle.importKey(
        "pkcs8",                          // PKCS#8 格式
        derBuffer,
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,                            // 不可导出
        ["decrypt"]
    )
}

/**
 * 解密后端下发的加密 Secret
 * @param encryptedBase64 - X-Next-Secret Header 的值（标准 Base64）
 * @returns 解密后的 secret 明文字符串
 */
export async function decryptSecret(encryptedBase64: string): Promise<string> {
    const privateKey = await importPrivateKey()

    // 标准 Base64 → Uint8Array
    const encryptedBytes = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0))

    const decryptedBytes = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        privateKey,
        encryptedBytes
    )

    return new TextDecoder("utf-8").decode(decryptedBytes)
}